import AdvancedSearchRequestBuilder from 'src/components/BeaconV2Components/utils/AdvancedSearchRequestBuilder';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { useAppSelector, useComparisonPeriod, useLocation } from 'src/utils/Hooks';
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _merge from 'lodash/merge';
import {
  computeComparisonStartWeek,
  getLastDayPreviousYear
} from 'src/utils/dateformatting';
import { getComparisonWeekId } from 'src/utils/dateUtils';
import { getRetailerIdDisplayName } from 'src/utils/stringFormatting';
import { getDefaultExportConfig, maybeOverrideAggregationFields } from 'src/components/Export/Export';
import { propEq } from 'src/utils/fp';
import {
  ExportConfig,
  makeGroupByConsistent
} from 'src/components/BeaconV2Components/common/AppExportModal/exportDataUtils';
import axios from 'axios';
import {
  ExportDropdownOptionIds,
  atlasV2ExportAggregationFieldsMatcher,
  exportAggregationFieldsMatcher
} from 'src/components/BeaconV2Components/common/AppExportModal/exportDataConfigData';
import {
  displayNameByFieldName,
  getDoubleAggregatableFieldNames,
  getExportGroupByFieldNames
} from 'src/components/Export/exportGroupByFields';
import { getAppName, shouldShowNewAtlas, shouldShowNewBeacon } from 'src/utils/app';
import { trackExport } from 'src/utils/mixpanel';
import {
  useApplyEntityFilters,
  useCreateRemoveRetailPriceRangeFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { BuildExportRequestFunction } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import { AdvancedSearchQuery } from 'sl-api-connector/search';
import { lifetimeAggregationFields } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewConstants';
import { AppName } from 'sl-api-connector';
import { useAtlasV2ApplyEntityFilters } from 'src/components/AtlasV2Components/Hooks/useAtlasV2BaseMetricRequestBuilder';
import { ATLAS_SUBTABS, ATLAS_TABS } from 'src/components/AtlasV2Components/AtlasV2Sidebar/useAtlasV2Routes';

// This is used during Cypress tests to generate custom export filenames that are easily identifiable
const useGenerateFileName = (prefix: string, request: ExportConfig[]) => {
  const { startWeek: mainStartWeek, endWeek: mainEndWeek } = useAppSelector((state) => state.mainTimePeriod);

  const retailer = useAppSelector((state) => state.retailer);
  const app = useAppSelector((state) => state.app);
  const entityService = useAppSelector((state) => state.entityService);

  if (window.localStorage.exportFileNameOverride) {
    return window.localStorage.exportFileNameOverride;
  }

  let retailerName = '';
  if (request.length > 0) {
    const [requestObj] = request;
    if (requestObj && requestObj.retailerId) {
      retailerName = getRetailerIdDisplayName(requestObj.retailerId, retailer);
    }
  }

  const typeAbbreviations: Record<string, string> = { subcategory: 'subcat', category: 'cat' };
  const type = typeAbbreviations[entityService?.mainEntity?.type] || entityService?.mainEntity?.type;
  const name = entityService?.mainEntity?.shortDisplayName;
  const { tab = app.defaultQueryParams.tab, subtab = app.defaultQueryParams.subtab } = app.queryParams;

  // Default: use main time period start and end week
  let startWeek = mainStartWeek;
  let endWeek = mainEndWeek;

  // Override the field name with the weekId filter, if there is one
  _get(request, ['0', 'aggregations', '0', 'conditions', 'rangeFilters'], []).forEach((filter) => {
    if (filter.fieldName === 'weekId') {
      startWeek = filter.minValue;
      endWeek = filter.maxValue;
    }
  });

  const fileName = `${prefix ? `${prefix} ` : ''}${type} ${
    name || ''
  } ${subtab && subtab !== 'keymetrics' ? subtab : tab} ${retailerName} ${startWeek} ${endWeek}`;
  const sanitized = fileName.replace(/ /g, '_').replace('V2', '').replace('V3', '').replace(/_+/g, '_');
  const suffix = `_${startWeek}_${endWeek}`;
  return sanitized.length > 150 ? `${sanitized.slice(0, 150 - suffix.length)}${suffix}` : sanitized;
};

// In Beacon V1, there is a default request from the table.
export const useDefaultExportRequest = ({
  groupByField = 'stacklineSku',
  shouldAddCompare = false,
  aggregationOverride
}: {
  groupByField: string;
  shouldAddCompare: boolean;
  aggregationOverride?: string;
}) => {
  const { queryParams } = useAppSelector((state) => state.app);
  const { categories } = useAppSelector((state) => state);
  const categoryIds = categories.map((category) => category.id);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const applyEntityFilters = useApplyEntityFilters();
  const applyAtlasEntityFilter = useAtlasV2ApplyEntityFilters();
  const appName = getAppName();

  const retailerId = useAppSelector((state) => state.retailer.id);
  const { tab, subtab, sw: startWeek, ew: endWeek } = queryParams;
  const removeRetailPriceFilters = useCreateRemoveRetailPriceRangeFilters();

  const aggregationMatcherToUse =
    appName === AppName.Atlas ? atlasV2ExportAggregationFieldsMatcher() : exportAggregationFieldsMatcher();

  let aggregationFieldMap = tab && subtab ? aggregationMatcherToUse?.[tab]?.[subtab] : aggregationMatcherToUse.default;

  if (tab === 'contentV2' && appName === AppName.Beacon) {
    aggregationFieldMap = aggregationMatcherToUse?.contentV2;
  }

  if (tab === 'contentV3') {
    aggregationFieldMap = aggregationMatcherToUse?.contentV3;
  }

  if (aggregationFieldMap?.shouldExportMultipleRequest && aggregationOverride) {
    aggregationFieldMap = aggregationFieldMap[aggregationOverride];
  }

  const {
    indexName,
    aggregationList,
    sortByField,
    derivedFieldsJson,
    aggregationSortByField,
    exportExtraConditions,
    exportExtraRangeFilterConditions,
    searchBy,
    shouldUserNullAggregation,
    shouldDoAggregationFalse,
    shouldOverrideConditionFilter,
    sortByOnlyFieldNames,
    isReturnDocument,
    period,
    shouldUseSortByAggregationFieldNull,
    setSortDirectionAsNull
  } = aggregationFieldMap || {
    indexName: `${appName}-sales`,
    aggregationList: [['Retail Sales', 'retailSales', 'sum', true]],
    sortByField: ['Retail Sales', 'retailSales', 'sum', true]
  };

  if (!aggregationFieldMap) {
    console.error(
      `Fallback export config is being used. Please replace it with the correct values. Please configure exports for ${tab} ${subtab}`
    );
  }

  const requestBuilder = new AdvancedSearchRequestBuilder(
    'entityGridMetricsentitypagecontainer_trendingProducts_5-product--1',
    indexName
  );

  let exportAggregationBuilder;

  if (aggregationList) {
    exportAggregationBuilder = new AggregationBuilder(groupByField);
    aggregationList.forEach(
      ([aggregationDisplayName, aggregationName, aggregationFunction, canBeExported, aggregateByFormula]) => {
        exportAggregationBuilder.addAggregationField(
          aggregationDisplayName,
          aggregationName,
          aggregationFunction,
          canBeExported,
          aggregateByFormula
        );
      }
    );

    exportAggregationBuilder.addConditionTermFilter('retailerId', [retailerId]);
    exportAggregationBuilder.addConditionRangeFilter('weekId', startWeek, endWeek);

    if (shouldAddCompare) {
      exportAggregationBuilder.addComparisonRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek);
    }

    const [sortDisplayName, sortName, sortFunc, sortCanBeExported] =
      aggregationSortByField || sortByField || aggregationList[0];
    exportAggregationBuilder.setSortByAggregationField(sortDisplayName, sortName, sortFunc, sortCanBeExported);
    // some exports (e.g - review need to have sortByAggregationField as null)
    if (shouldUseSortByAggregationFieldNull) {
      exportAggregationBuilder.setSortByAggregationFieldAsNull();
    }
    exportAggregationBuilder.setSortDirection('desc');
    requestBuilder.addAggregation(exportAggregationBuilder.build());
  }

  // Currently Review should have aggregation as null.
  if (shouldUserNullAggregation) {
    requestBuilder.makeNullAggregations();
  }

  if (setSortDirectionAsNull) {
    requestBuilder.setSortDirectionAsNull();
  }

  requestBuilder
    .setAdditionalRequestMetaDataDerivedFieldsJson(derivedFieldsJson || '[]')
    .setProcessDocuments(true)
    .setPageNumber(1)
    .setPageSize(20)
    .setRetailerId(String(retailerId))
    .setPeriod(period || 'period')
    .setDoAggregation(!shouldDoAggregationFalse)
    .setReturnDocuments(isReturnDocument || false)
    .setSearchBy(searchBy || 'parent')
    .addConditionTermFilter('retailerId', 'should', [retailerId])
    .addConditionTermFilter('categoryId', 'should', categoryIds);

  if (shouldShowNewBeacon()) {
    requestBuilder.apply(applyEntityFilters).apply(removeRetailPriceFilters());
  }

  if (shouldShowNewAtlas()) {
    requestBuilder.apply(applyAtlasEntityFilter);
  }

  if (sortByField) {
    const [sortDisplayName, sortName, sortFunc, sortCanBeExported] = sortByField;
    requestBuilder.addSortField(sortDisplayName, sortName, sortFunc, sortCanBeExported);
  }

  if (exportExtraConditions) {
    exportExtraConditions.forEach(({ fieldName, values }) =>
      requestBuilder.addConditionTermFilter(fieldName, 'should', values)
    );
  }

  if (exportExtraRangeFilterConditions) {
    exportExtraRangeFilterConditions.forEach(({ fieldName, minValue, maxValue }) => {
      requestBuilder.addConditionRangeFilter(fieldName, minValue, maxValue);
    });
  }

  if (shouldOverrideConditionFilter) {
    requestBuilder.replaceConditionRangeFilter('weekId', startWeek, endWeek);
  }

  if (sortByOnlyFieldNames) {
    requestBuilder.clearSortFields();
    sortByOnlyFieldNames.forEach((fieldName) => requestBuilder.addSortFieldWithOnlyFieldName(fieldName, 'desc'));
  }

  return requestBuilder.build();
};

// some exports we need to send two different request
export const buildMultipleRequests = ({
  groupByFieldName,
  includeCompTimePeriod,
  fieldElements
}: {
  groupByFieldName: string;
  includeCompTimePeriod: boolean;
  fieldElements: string[];
}) => {
  const requestList = [];
  fieldElements.forEach((element) => {
    const exportRequest = useDefaultExportRequest({
      groupByField: groupByFieldName,
      shouldAddCompare: includeCompTimePeriod,
      aggregationOverride: element
    });
    requestList.push(exportRequest);
  });

  return requestList;
};

export const useExportDataFunction = ({
  type,
  groupByFieldName,
  includeCompTimePeriod,
  buildExportRequest
}: {
  type?: string;
  groupByFieldName: string;
  includeCompTimePeriod: boolean;
  buildExportRequest?: BuildExportRequestFunction;
}) => {
  const app = useAppSelector((state) => state.app);
  const pid = useComparisonPeriod();
  const { tab, subtab } = app.queryParams;
  const retailerId = useAppSelector((state) => state.retailer.id);
  const allWeekIdsByRetailerId = useAppSelector((state) => state.allWeekIdsByRetailerId);
  const weekIds = allWeekIdsByRetailerId[retailerId];
  const {
    id: mainTimePeriodId,
    endWeek: mainTimePeriodEndWeek,
    startWeek: mainTimePeriodStartWeek
  } = useAppSelector((state) => state.mainTimePeriod);
  const location = useLocation();
  const user = useAppSelector((state) => state.user);
  const appName = getAppName();

  const { mainEntity } = useAppSelector((state) => state.entityService);

  let exportRequest = useDefaultExportRequest({
    groupByField: groupByFieldName,
    shouldAddCompare: includeCompTimePeriod
  });

  const aggregationMatcherToUse = shouldShowNewAtlas()
    ? atlasV2ExportAggregationFieldsMatcher()
    : exportAggregationFieldsMatcher();

  const aggregationFieldMap =
    tab && subtab ? aggregationMatcherToUse?.[tab]?.[subtab] : aggregationMatcherToUse.default;

  if (aggregationFieldMap?.shouldExportMultipleRequest) {
    exportRequest = buildMultipleRequests({
      groupByField: groupByFieldName,
      fieldElements: aggregationMatcherToUse?.[tab]?.[subtab].shouldExportMultipleRequest,
      includeCompTimePeriod
    });
  }

  const defaultExport = Array.isArray(exportRequest) ? exportRequest : [exportRequest];

  const request = _cloneDeep(defaultExport);
  // For YTD + prior-period, only set dayId comparison max here; weekId is resolved in export callback via API
  if (mainTimePeriodId === 'ytd' && pid === 'prior-period') {
    const firstComparisonRangeFilter = _get(request, '[0].aggregations[0].comparisonRangeFilters[0]');
    if (firstComparisonRangeFilter?.fieldName === 'dayId') {
      firstComparisonRangeFilter.maxValue = getLastDayPreviousYear(weekIds);
    }
  }

  const entityType = _get(mainEntity, ['type'], 'noType');
  const entityDisplayName = _get(mainEntity, ['displayName'], entityType);
  const entityName = _get(mainEntity, ['name'], 'no name');

  let requestOverride = [{}];

  if (type === 'Master' || type === 'Current') {
    requestOverride[0] = {
      sortFilter: null,
      returnDocuments: true,
      searchType: type === 'Master' ? 'beacon-contentApproved' : 'beacon-contentCurrent',
      doAggregation: false,
      searchBy: 'child',
      conditions: { rangeFilters: request[0].aggregations?.[0].conditions?.rangeFilters }
    };
  } else if (type === 'ContentCombine') {
    // Change to most recent week
    request[0].aggregations[0].conditions.rangeFilters.forEach((filter) => {
      if (filter.fieldName === 'weekId') {
        // prev commit from export says "Use most recent week in master content export"
        filter.minValue = mainTimePeriodEndWeek;
        filter.maxValue = mainTimePeriodEndWeek;
      }
    });
    requestOverride = [
      {
        sortFilter: null,
        returnDocuments: true,
        searchType: 'beacon-contentApproved',
        doAggregation: false,
        searchBy: 'child',
        conditions: { rangeFilters: request[0].aggregations[0].conditions.rangeFilters }
      }
    ];
  } else if (['Unaggregated', 'Aggregated'].includes(type)) {
    if (request[0].aggregations[0].groupByFieldName === 'searchKeyword,matchingType') {
      request[0].searchType = 'advertising-adKeywordDailyMetrics';
      request[0].pageSize = 500;
    }
    request[0].aggregations[0].aggregationFields.forEach((field) => {
      if (field.aggregateByFieldDisplayName === 'Campaign Name' && field.aggregateByFieldName === 'campaignId') {
        field.aggregateByFieldDisplayName = 'No of Campaigns';
      }
    });

    request[0].additionalRequestMetaData = request[0].additionalRequestMetaData || {};
    if (groupByFieldName) {
      request[0].additionalRequestMetaData = {
        ...request[0].additionalRequestMetaData,
        EntityColumnToExport: JSON.stringify({ [entityDisplayName]: entityName }),
        Aggregations: { groupByField: [groupByFieldName] }
      };
    } else {
      request[0].additionalRequestMetaData = {
        ...request[0].additionalRequestMetaData,
        EntityColumnToExport: JSON.stringify({ [entityDisplayName]: entityName })
      };
    }

    if (type === 'Aggregated') {
      const requestClone = _cloneDeep(request[0]);
      requestClone.aggregations[0].conditions.rangeFilters = requestClone.aggregations[0].comparisonRangeFilters;
      request.push(requestClone);
    } else if (type === 'Unaggregated') {
      request[0].aggregations[0].groupByFieldName += ',dayId';
      request[0].conditions.rangeFilters = request[0].aggregations[0].conditions.rangeFilters;
    }
  }

  let fileNamePrefix;
  if (type === 'Master') {
    fileNamePrefix = 'MasterContent';
  } else if (type === 'Current') {
    fileNamePrefix = 'LiveCurrentContent';
  } else if (type === 'ContentCombine') {
    fileNamePrefix = 'MasterContent';
  } else if (['Unaggregated', 'Aggregated'].includes(type)) {
    fileNamePrefix = type;
  }

  // Enable break down by week even if there is a last value main metric, e.g. unitsOnHand
  // This way we can export multiple weeks but only display most recent in the UI
  // If we want to disable this for an export, we should disable the "Separate data by week" for the export

  if (groupByFieldName && groupByFieldName.includes('weekId')) {
    _get(request, [0, 'aggregations', 0, 'conditions', 'rangeFilters'], []).forEach((filter) => {
      if (filter.fieldName === 'weekId') {
        filter.minValue = mainTimePeriodStartWeek;
        filter.maxValue = mainTimePeriodEndWeek;
      }
    });
  }

  const fileName = useGenerateFileName(fileNamePrefix, request);
  const defaultExportConfig = getDefaultExportConfig(fileName);

  const mergedRequest = _merge([], defaultExportConfig, request, requestOverride);

  if (Array.isArray(mergedRequest[0].aggregations) && mergedRequest[0].aggregations[0] && groupByFieldName) {
    mergedRequest[0].aggregations[0].groupByFieldName = groupByFieldName;
  }

  if (_get(mergedRequest, '[0].searchBy') === 'child') {
    mergedRequest[0].returnParentDocuments = true;
  }

  // Remove comparison time period if the user hasn't opted to include it in their export
  const hasComparisonTimePeriod = _get(mergedRequest, '[0].aggregations[0].comparisonRangeFilters');
  if (hasComparisonTimePeriod && !includeCompTimePeriod) {
    mergedRequest.forEach((req) => {
      delete req.aggregations[0].comparisonRangeFilters;
    });
  }

  if (mergedRequest[0] && mergedRequest[0].doAggregation && !!mergedRequest[0].aggregations) {
    maybeOverrideAggregationFields(
      app.name,
      mergedRequest,
      tab || app.defaultQueryParams.tab,
      subtab || app.defaultQueryParams.subtab
    );
  }

  // Add some additional fields to indicate that this is to use the new exports pipeline
  mergedRequest.forEach((req) => {
    if (!req.additionalRequestMetaData) {
      req.additionalRequestMetaData = {};
    }
    req.additionalRequestMetaData.useNewExports = 'True';
    // Used by segment export
    req.entityType = entityType;
    req.entityName = entityName;
  });

  mergedRequest[0].tab = tab;
  mergedRequest[0].subtab = subtab;

  makeGroupByConsistent(mergedRequest, groupByFieldName);

  const updatedMergedRequest = buildExportRequest
    ? buildExportRequest({ originalRequest: mergedRequest, groupByFieldName, includeCompTimePeriod })
    : mergedRequest;
  const exportCallBack = async () => {
    let requestToSend = updatedMergedRequest;
    const hasComparisonInRequest = _get(updatedMergedRequest, '[0].aggregations[0].comparisonRangeFilters');
    // YTD + prior-period: resolve weekId comparison range via API (never -100; 202553 -> valid prior-year week)
    if (
      hasComparisonInRequest &&
      includeCompTimePeriod &&
      mainTimePeriodId === 'ytd' &&
      pid === 'prior-period'
    ) {
      const comparisonEndWeek = await getComparisonWeekId(mainTimePeriodEndWeek);
      const comparisonStartWeek = computeComparisonStartWeek(
        mainTimePeriodStartWeek,
        mainTimePeriodEndWeek,
        comparisonEndWeek
      );
      requestToSend = _cloneDeep(updatedMergedRequest);
      requestToSend.forEach((req: ExportConfig) => {
        const comparisonRangeFilters = _get(req, 'aggregations[0].comparisonRangeFilters');
        if (Array.isArray(comparisonRangeFilters)) {
          comparisonRangeFilters
            .filter(propEq('fieldName', 'weekId'))
            .forEach((compRangeFilter: { minValue: number; maxValue: number }) => {
              compRangeFilter.minValue = comparisonStartWeek;
              compRangeFilter.maxValue = comparisonEndWeek;
            });
        }
      });
    }
    return axios({
      method: 'post',
      url: `/api/${appName}/AdvancedSearchExport`,
      data: requestToSend
    }).then(() => {
      trackExport(location, {}, user);
    });
  };

  return { exportRequestFunction: exportCallBack };
};

// https://stackline.atlassian.net/wiki/spaces/~63644528d66d8108a125f792/pages/1958084798/Compare+Export+Functionality

export const useExportDropdownOption = () => {
  const { queryParams } = useAppSelector((state) => state.app);
  const entityType = useAppSelector((state) => state?.entityService?.mainEntity?.type);
  const { tab, subtab } = queryParams;
  const appName = getAppName();

  const { groupByFieldNames, isAggregatable } = getExportGroupByFieldNames({
    appName,
    entityType,
    tab,
    subtab
  });

  const dropdownOptions = groupByFieldNames.map((fieldName) => ({
    label: displayNameByFieldName.get(fieldName) || fieldName,
    id: fieldName as ExportDropdownOptionIds
  }));

  return {
    dropdownOptions,
    isAggregatable
  };
};

/**
 * On the export modal, there is a section called customized, and it should be dynamically rendered by tab, subtab, and what is selected on the dropdown
 * to add or modify this, check BaseDoubleAggregatableFieldNames in exportGroupByFields.ts file.
 */

const shouldHideCustomizeSectionConfig = (tab, subtab) => {
  const config = {
    [AppName.Beacon]: {
      [BEACON_TABS.SALES]: [BEACON_SUBTABS.GROWTH_MATRIX]
    },
    [AppName.Atlas]: {
      [ATLAS_TABS.PRICES]: ATLAS_SUBTABS.PROMOTIONS
    }
  };

  const appName = getAppName();
  return config[appName]?.[tab]?.includes(subtab) || false;
};
export const useExportCustomOptionBuilder = (
  selectedGroupBy: ExportDropdownOptionIds | string = ExportDropdownOptionIds.PRODUCT
) => {
  // based on the selectedGroupBy we will show or hide Separate data by weekId option.
  const TABS = [BEACON_TABS.BUY_BOX, BEACON_TABS.SALES, BEACON_TABS];
  const SUBTABS = [BEACON_SUBTABS.SELLERS, BEACON_SUBTABS.PROMOTIONS];

  const { tab, subtab } = useAppSelector((state) => state.app.queryParams);
  const getGroupByWeekAvailableFunction = (groupBy): boolean => {
    return getDoubleAggregatableFieldNames(getAppName()).has(groupBy);
  };

  const shouldDisableComparisonPeriod = shouldShowNewBeacon() && selectedGroupBy.includes('reviews');

  // buybox and seller should always have forceCheckBox - https://gitlab.com/stackline-dev/website/-/commit/c29d881e7eac2d723a1bc736d7bd5907ce7583f2
  const forceWeekSeparation =
    (TABS.includes(tab) && SUBTABS.includes(subtab)) ||
    (BEACON_TABS.REVIEWS && selectedGroupBy === ExportDropdownOptionIds.REVIEW);

  // we can list special export - currently product growth should hide all Customize section
  const shouldHideCustomizeSection = shouldHideCustomizeSectionConfig(tab, subtab);

  return {
    getGroupByWeekAvailableFunction,
    forceWeekSeparation,
    shouldDisableComparisonPeriod,
    shouldHideCustomizeSection
  };
};

export const exportReviewProduct = ({
  groupByFieldName,
  includeCompTimePeriod,
  originalRequest,
  startWeek,
  endWeek,
  latestCumulativeWeekId,
  comparisonEndWeek,
  mainEntityName,
  mainEntityType,
  createRequestBuilder
}: {
  groupByFieldName: string;
  includeCompTimePeriod: boolean;
  originalRequest: AdvancedSearchQuery;
  startWeek: number;
  endWeek: number;
  latestCumulativeWeekId: number;
  comparisonStartWeek: number;
  comparisonEndWeek: number;
  mainEntityName: string;
  mainEntityType: string;
  createRequestBuilder: AdvancedSearchRequestBuilder;
}) => {
  // original request is coming from the Beacon V1 Review/ Review Trends, so we are just replacing it here.
  const isSeparateByWeek = groupByFieldName.includes('weekId');

  const { fileName } = originalRequest[0];
  const weeklyRequest = createRequestBuilder;

  weeklyRequest
    .setReturnDocuments(false)
    .setDoAggregation(true)
    .setExportResult(true)
    .replaceConditionRangeFilter('weekId', startWeek, endWeek)
    .setCustomAdditionalMetadata({ derivedFieldsJson: '[]', useNewExports: 'True' })
    .setEntityName(mainEntityName)
    .setEntityType(mainEntityType)
    .setFileName(fileName)
    .setProcessDocuments(true);

  const lifetimeRequest = weeklyRequest.clone();

  const lifetimeAggregation = new AggregationBuilder('stacklineSku');

  lifetimeAggregationFields.forEach((fieldName) =>
    lifetimeAggregation.addAggregationFieldsByIndexAndField(BEACON_INDEX_NAMES.RATINGS_REVIEWS_METRICS_V2, fieldName)
  );
  lifetimeAggregation
    .setSortByAggregationField('Lifetime Rating Volume', 'parentLifetimeRatingVolume', 'sum', true)
    .replaceConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId);

  if (includeCompTimePeriod) {
    lifetimeAggregation.replaceComparisonRangeFilter('weekId', comparisonEndWeek, comparisonEndWeek);
  }
  lifetimeRequest.clearAggregations().addAggregation(lifetimeAggregation.build());

  return isSeparateByWeek ? [weeklyRequest.request] : [weeklyRequest.request, lifetimeRequest.request];
};
